import type {
  BridgeItem,
  CrownItem,
  InlayCoreItem,
  Item,
  LayItem,
  RetentionWireItem,
  ToothScopedItem,
  SpaceMaintainerItem,
  VeneerItem,
  WaxBiteItem,
  CustomTrayItem,
  OrthoModelItem,
  DentureRepairItem,
  AdditionalImpressionForRemovableProsthesisItem,
  AddingTeethOnExistingDentureItem,
  WaxUpItem,
  ImplantCrownItem,
  AbutmentItem,
  ImplantBridgeItem,
  BridgePonticItem,
} from "../items";

export const ProductTypeEnum = {
  bridge: "BRIDGE",
  crown: "CROWN",
  lay: "LAY",
  inlay: "INLAY",
  onlay: "ONLAY",
  overlay: "OVERLAY",
  inlayCore: "INLAY_CORE",
  postAndCoreRetainedCrown: "POST_AND_CORE_RETAINED_CROWN",
  veneer: "VENEER",
  implant: "IMPLANT",
  implantCrown: "IMPLANT_CROWN",
  abutment: "ABUTMENT",
  customAbutment: "CUSTOM_ABUTMENT",
  splint: "SPLINT",
  nightGuard: "NIGHT_GUARD",
  bleachingTray: "BLEACHING_TRAY",
  retainer: "RETAINER",
  removableDenture: "REMOVABLE_DENTURE",
  partialDenture: "PARTIAL_DENTURE",
  fullDenture: "FULL_DENTURE",
  fullDentureWaxBite: "FULL_DENTURE_WAX_BITE",
  immediateDenture: "IMMEDIATE_DENTURE",
  model: "MODEL",
  richmondCrown: "RICHMOND_CROWN",
  splintedCrown: "SPLINTED_CROWN",
  splintedCrownImplant: "SPLINTED_CROWN_IMPLANT",
  bridgeImplant: "BRIDGE_IMPLANT",
  bridgePontic: "BRIDGE_PONTIC",
  scanOnly: "SCAN_ONLY",
  spaceMaintainer: "SPACE_MAINTAINER",
  retentionWire: "RETENTION_WIRE",
  waxUp: "WAX_UP",
  waxBite: "WAX_BITE",
  additionalImpressionForRemovableProsthesis:
    "ADDITIONAL_IMPRESSION_FOR_REMOVABLE_PROSTHESIS",
  addingTeethOnExistingDenture: "ADDING_TEETH_ON_EXISTING_DENTURE",
  dentureRepair: "DENTURE_REPAIR",
  orthoModel: "ORTHO_MODEL",
  customTray: "CUSTOM_TRAY",
  metalMesh: "METAL_MESH",
} as const;

export type ProductType =
  (typeof ProductTypeEnum)[keyof typeof ProductTypeEnum];

export const ITEM_PREFERENCE_OPTIONS = {
  occlusalContactTightness: ["HEAVY", "MEDIUM", "LIGHT", "NONE"],
  proximalContactTightness: ["HEAVY", "MEDIUM", "LIGHT", "NONE"],
  noOcclusalClearance: ["KEEP_MANUFACTURING", "ASK_DENTIST"],
  occlusalStaining: ["DARK", "MEDIUM", "LIGHT", "NONE"],
  interproximalContactDesign: ["NORMAL_CONTACT_POINT", "WIDE_CONTACT_SURFACE"],
  anteriorPosteriorPonticDesign: ["MODIFIED_RIDGE_LAP", "FULL_RIDGE_LAP"],
} as const satisfies Record<string, readonly string[]>;

export type ItemPreferenceKey = keyof typeof ITEM_PREFERENCE_OPTIONS;

export type ItemPreferenceValue<K extends ItemPreferenceKey> =
  (typeof ITEM_PREFERENCE_OPTIONS)[K][number];

export type ItemPreferences = {
  [K in ItemPreferenceKey]?: ItemPreferenceValue<K> | null;
};

export interface Product {
  id: string;
  teeth: number[];
  type: ProductType;
  items: Item[];
  itemPreferences?: ItemPreferences | null;
}

export interface BridgeProduct extends Product {
  type: typeof ProductTypeEnum.bridge;
  items: BridgeItem[];
}

export interface VeneerProduct extends Product {
  type: typeof ProductTypeEnum.veneer;
  items: VeneerItem[];
}

export interface LayProduct extends Product {
  type: typeof ProductTypeEnum.lay;
  items: LayItem[];
}

export interface AddingTeethOnExistingDentureProduct extends Product {
  type: typeof ProductTypeEnum.addingTeethOnExistingDenture;
  items: AddingTeethOnExistingDentureItem[];
}

export interface CrownProduct extends Product {
  type: typeof ProductTypeEnum.crown;
  items: CrownItem[];
}

export interface SplintedCrownProduct extends Product {
  type: typeof ProductTypeEnum.splintedCrown;
  items: CrownItem[];
}

export interface InlayCoreProduct extends Product {
  type: typeof ProductTypeEnum.inlayCore;
  items: Array<InlayCoreItem | CrownItem>;
}

export interface WaxBiteProduct extends Product {
  type: typeof ProductTypeEnum.waxBite;
  items: WaxBiteItem[];
}

export interface RetentionWireProduct extends Product {
  type: typeof ProductTypeEnum.retentionWire;
  items: RetentionWireItem[];
}

export interface CustomTrayProduct extends Product {
  type: typeof ProductTypeEnum.customTray;
  items: CustomTrayItem[];
}

export type ItemMaterialCharacteristics = Pick<
  ToothScopedItem,
  "tooth" | "materialCharacteristics"
>;

export interface ProductMaterialCharacteristics {
  items: ItemMaterialCharacteristics[];
}

export type ItemInlayCoreCharacteristics = Pick<
  InlayCoreItem,
  "tooth" | "materialCharacteristics" | "inlayCoreCharacteristics"
>;

export interface ProductInlayCoreCharacteristics {
  items: ItemInlayCoreCharacteristics[];
}

export interface SpaceMaintainerProduct extends Product {
  type: typeof ProductTypeEnum.spaceMaintainer;
  items: SpaceMaintainerItem[];
  itemPreferences: undefined;
}

export interface OrthoModelProduct extends Product {
  type: typeof ProductTypeEnum.orthoModel;
  items: OrthoModelItem[];
  itemPreferences: undefined;
}

export interface AdditionalImpressionForRemovableProsthesisProduct extends Product {
  type: typeof ProductTypeEnum.additionalImpressionForRemovableProsthesis;
  items: AdditionalImpressionForRemovableProsthesisItem[];
  itemPreferences: undefined;
}

export interface DentureRepairProduct extends Product {
  type: typeof ProductTypeEnum.dentureRepair;
  items: DentureRepairItem[];
}

export interface WaxUpProduct extends Product {
  type: typeof ProductTypeEnum.waxUp;
  items: WaxUpItem[];
  itemPreferences?: undefined;
}

export interface ImplantCrownProduct extends Product {
  type: typeof ProductTypeEnum.implantCrown;
  items: Array<ImplantCrownItem | AbutmentItem>;
}

export interface ImplantSplintedCrownProduct extends Product {
  type: typeof ProductTypeEnum.splintedCrownImplant;
  items: Array<ImplantCrownItem | AbutmentItem>;
}

export interface ImplantBridgeProduct extends Product {
  type: typeof ProductTypeEnum.bridgeImplant;
  items: Array<ImplantBridgeItem | BridgePonticItem | AbutmentItem>;
}
